import type { SanityFinding } from '../../types/siteRag';
import { normaliseClaimTextForMatch } from '../renumberReferences';
import { splitSentences } from '../citationDensity';
import type { ReportClaim } from '../../types/citation';
import { resolveScopedClaimSources, type ScopedClaimSourceContext } from './scopedClaimSources';

// Accept the citation forms already emitted by the report writer, preserving
// their literal indices until the normal reference-finalization pass.
const REVIEW_REF_TOKEN = /\[Ref\s*(?::\s*)?\d+(?:\s*,\s*(?:Ref\s*(?::\s*)?)?\d+)*\]/gi;
const REVIEW_REF_GROUP = `${REVIEW_REF_TOKEN.source}(?:\\s*(?:,\\s*)?${REVIEW_REF_TOKEN.source})*`;
const REVIEW_REFS_BEFORE_PUNCTUATION = new RegExp(`\\s*${REVIEW_REF_GROUP}\\s*(?=[.,;:!?])`, 'gi');

function reviewedSentences(prose: string): string[] {
  const sentences: string[] = [];
  for (const part of splitSentences(prose)) {
    // Preserve citations placed after punctuation with the preceding claim.
    const leading = part.match(new RegExp(`^(?:${REVIEW_REF_TOKEN.source}[,\\s]*)+`, 'i'))?.[0];
    if (leading && sentences.length) {
      sentences[sentences.length - 1] += ` ${leading.trim()}`;
      const rest = part.slice(leading.length).trim();
      if (rest) sentences.push(rest);
    } else if (sentences.length && /\bU\.S\.$/.test(sentences[sentences.length - 1]!)
      && /^(?:Army|Department|Environmental|Nuclear|Geological|Fish|Forest|government)\b/.test(part)) {
      sentences[sentences.length - 1] += ` ${part}`;
    } else sentences.push(part);
  }
  return sentences;
}

export const EXECUTIVE_SUMMARY_TRACEABILITY_FINDING = 'exec-summary-claims-need-reconciliation';

/** Synchronize the claim ledger after an accepted prose correction. This
 * records citation attachments, not factual verification: downstream density,
 * citation-support, grounding and truth checks must still judge the new text.
 * Uncited sentences remain explicit uncited claims; stale sources are never
 * inherited by a changed sentence. An empty or wholly untraceable summary rewrite
 * leaves the old ledger intact so the existing unresolved flag survives.
 * Uncited nested corrections replace stale claims and remain subject to density checks. */
export function reconcileReviewedProseClaims(
  original: string,
  reviewed: string,
  claims: ReportClaim[] | undefined,
  referenceCount: number,
  sectionId = 'executiveSummary',
  sourceContext?: ScopedClaimSourceContext,
): ReportClaim[] | null {
  if (!original || original === reviewed || !reviewed.trim() || !Array.isArray(claims)) return null;
  // A prose token before punctuation is absent from a ledger claim whose
  // attachment is stored in sources[]. Remove only its surrounding whitespace
  // so an accepted correction cannot leave the old assertion behind.
  const key = (text: string) => text.replace(REVIEW_REFS_BEFORE_PUNCTUATION, '')
    .replace(new RegExp(REVIEW_REF_GROUP, 'gi'), '').replace(/\s+/g, ' ').trim();
  const originalKey = key(original);
  // Older ledgers store a parent section for a specific nested sentence.
  // Match only a true path ancestor AND text actually present in this leaf;
  // similarly named sections and explicit sibling paths remain untouched.
  // Editorial prose also uses the named report section in legacy ledgers.
  const editorialSection = /^editorialNarrative\.sections\.([A-Za-z][A-Za-z0-9_]*)\.editorialNarrative$/.exec(sectionId)?.[1];
  const belongsToReviewedField = (claimSection: string) => claimSection === sectionId
    || claimSection === editorialSection
    || (claimSection.length > 0
      && (sectionId.startsWith(`${claimSection}.`) || sectionId.startsWith(`${claimSection}[`)));
  const previous = claims.filter(claim => typeof claim?.sectionId === 'string' && belongsToReviewedField(claim.sectionId)
    && typeof claim.text === 'string' && key(claim.text)
    && (sectionId === 'executiveSummary' || originalKey.includes(key(claim.text))));
  if (!previous.length) return null;
  const literalKey = (text: string) => text.replace(/\s+/g, ' ').trim();
  const originalSentences = new Set(reviewedSentences(original).map(literalKey));
  const reviewedParts = reviewedSentences(reviewed);
  const unchangedParts = sectionId === 'executiveSummary' ? []
    : reviewedParts.filter(text => originalSentences.has(literalKey(text)));
  // Existing ledger entries may cover only a clause of an untouched sentence.
  // Keep those original records without expanding them into new whole-sentence claims.
  const preserved = new Set(previous.filter(claim => unchangedParts.some(text => key(text).includes(key(claim.text)))));
  // Nested corrections do not create new ledger entries for untouched prose
  // elsewhere in the field (including deterministic phase-water disclosures).
  // Every changed/new sentence still enters the ledger, including uncited text.
  const sentences = reviewedParts.filter(text => sectionId === 'executiveSummary'
    || !originalSentences.has(literalKey(text)));
  let invalidReference = false;
  const replacement: ReportClaim[] = sentences.map(text => {
    const refs = [...text.matchAll(REVIEW_REF_TOKEN)].flatMap(match => [...match[0].matchAll(/\d+/g)].map(index => Number(index[0])));
    if (refs.some(index => !Number.isSafeInteger(index) || index < 1 || index > referenceCount)) invalidReference = true;
    const unchanged = previous.find(claim => typeof claim.text === 'string' && key(claim.text) === key(text));
    const scopedSources = resolveScopedClaimSources(text, sectionId, sourceContext);
    return {
      ...unchanged,
      sectionId, text,
      sources: refs.length ? [...new Set(refs)].map(index => `ref:${index}`).concat(unchanged?.sources?.filter(source => typeof source === 'string' && !source.startsWith('ref:')) ?? [])
        : unchanged?.sources?.filter(source => typeof source === 'string').length
          ? unchanged.sources.filter(source => typeof source === 'string') : scopedSources,
    };
  });
  if (invalidReference || (sectionId === 'executiveSummary' && !replacement.some(claim => claim.sources.length > 0))) return null;
  const replaced = new Set(previous.filter(claim => !preserved.has(claim)));
  return [...claims.filter(claim => !replaced.has(claim)), ...replacement];
}

/** Draft claims establish traceability, not factual authority over a correction. */
export function checkReviewedExecutiveSummary(
  original: string,
  reviewed: string,
  claims: unknown,
): SanityFinding | null {
  if (!original || original === reviewed || !Array.isArray(claims)) return null;
  // Reference numbering is checked separately; changing a marker alone is
  // not a wholesale substitution of the narrative.
  const matchKey = (text: string) => normaliseClaimTextForMatch(text.replace(/\[Ref\s+\d+\]/gi, '').trim());
  const executiveClaims = claims.filter((claim): claim is { text: string } =>
    claim?.sectionId === 'executiveSummary'
    && typeof claim.text === 'string'
    && matchKey(claim.text).length > 0);
  const matches = (body: string) => {
    const text = matchKey(body);
    return executiveClaims.some(claim => text.includes(matchKey(claim.text)));
  };
  if (!matches(original) || matches(reviewed)) return null;
  return {
    id: EXECUTIVE_SUMMARY_TRACEABILITY_FINDING,
    category: 'inconsistency',
    section: 'executiveSummary',
    finding: 'The reviewed executive summary no longer matches the original draft claims. Its source traceability requires review; this alone does not establish that the correction is inaccurate or off topic.',
    suggestion: 'Check the reviewed summary against the SED and cited documents, then reconcile its claims and citations. The reviewed text is retained; the original draft is not restored as evidence.',
    severity: 'high',
  };
}
